import { apiService } from './api';
import { NotificationData, NotificationType, NotificationPriority } from '../types/notification';

export interface NotificationResponse {
  notifications: NotificationData[];
  total: number;
  unreadCount: number;
}

export interface CreateNotificationRequest {
  title: string;
  message: string;
  type: NotificationType;
  priority: NotificationPriority;
  actionUrl?: string;
  actionLabel?: string;
  metadata?: Record<string, any>;
}

class NotificationService {
  private eventSource: EventSource | null = null;
  private pollingInterval: ReturnType<typeof setInterval> | null = null;
  private readonly localApiFallback = process.env.REACT_APP_LOCAL_API_URL || 'http://localhost:3002';

  private resolveRealtimeBaseUrl(): string {
    const configured = process.env.REACT_APP_API_URL || 'http://localhost:3002';
    const isCloudflareTunnel = /trycloudflare\.com/i.test(configured);
    const isLocalBrowser =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    if (isCloudflareTunnel && isLocalBrowser) {
      return this.localApiFallback;
    }

    return configured;
  }

  // ✅ helper: detect heartbeat / keep-alive messages from backend
  private isHeartbeat(payload: any): boolean {
    const title = String(payload?.title ?? '').toLowerCase();
    const message = String(payload?.message ?? '').toLowerCase();
    const type = String(payload?.type ?? '').toLowerCase();

    // common patterns
    if (type === 'heartbeat' || type === 'keepalive' || type === 'keep-alive') return true;
    if (title.includes('heartbeat') || message.includes('heartbeat')) return true;
    if (title.includes('keep alive') || message.includes('keep alive')) return true;
    if (title.includes('keepalive') || message.includes('keepalive')) return true;

    return false;
  }

  // ✅ helper: validate minimal notification shape
  private isValidNotification(payload: any): payload is NotificationData {
    // adjust if your NotificationData uses _id instead of id
    const hasId = typeof payload?.id === 'string' || typeof payload?._id === 'string';
    const hasTitle = typeof payload?.title === 'string';
    const hasMessage = typeof payload?.message === 'string';
    return hasId && hasTitle && hasMessage;
  }

  // Helper: transform API notification to frontend format
  private transformNotification(apiNotification: any): NotificationData {
    return {
      id: apiNotification._id || apiNotification.id,
      title: apiNotification.title,
      message: apiNotification.message,
      type: apiNotification.type,
      priority: apiNotification.priority,
      isRead: apiNotification.isRead || false,
      createdAt: new Date(apiNotification.createdAt),
      actionUrl: apiNotification.actionUrl,
      actionLabel: apiNotification.actionLabel,
      metadata: apiNotification.metadata,
    };
  }

  // Fetch notifications from API
  async getNotifications(page = 1, limit = 50): Promise<NotificationResponse> {
    try {
      const response = await apiService.get<any>(`/api/notifications?page=${page}&limit=${limit}`);
      const data = response.data?.data || { notifications: [], total: 0 };
      
      // Transform notifications to ensure id field is properly set
      const notifications = (data.notifications || []).map((n: any) => this.transformNotification(n));
      
      return {
        notifications,
        total: data.total || 0,
        unreadCount: data.unread || 0,
      };
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      return { notifications: [], total: 0, unreadCount: 0 };
    }
  }

  // Get unread notifications count
  async getUnreadCount(): Promise<number> {
    try {
      const response = await apiService.get<any>('/api/notifications/unread-count');
      return response.data?.data?.count || 0;
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
      return 0;
    }
  }

  // Mark notification as read
  async markAsRead(notificationId: string): Promise<boolean> {
    try {
      await apiService.put(`/api/notifications/${notificationId}/read`, {});
      return true;
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      return false;
    }
  }

  // Mark all notifications as read
  async markAllAsRead(): Promise<boolean> {
    try {
      await apiService.put('/api/notifications/read-all', {});
      return true;
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      return false;
    }
  }

  // Delete notification
  async deleteNotification(notificationId: string): Promise<boolean> {
    try {
      await apiService.delete(`/api/notifications/${notificationId}`);
      return true;
    } catch (error) {
      console.error('Failed to delete notification:', error);
      return false;
    }
  }

  // Clear all notifications
  async clearAllNotifications(): Promise<boolean> {
    try {
      await apiService.delete('/api/notifications/clear-all');
      return true;
    } catch (error) {
      console.error('Failed to clear all notifications:', error);
      return false;
    }
  }

  // Create notification (for testing or admin use)
  async createNotification(notification: CreateNotificationRequest): Promise<NotificationData | null> {
    try {
      const response = await apiService.post<any>('/api/notifications', notification);
      const data = response.data?.data;
      return data ? this.transformNotification(data) : null;
    } catch (error) {
      console.error('Failed to create notification:', error);
      return null;
    }
  }

  // Start real-time updates using Server-Sent Events
  startRealTimeUpdates(
    onNotification: (notification: NotificationData) => void,
    onError?: (error: Event) => void
  ): void {
    // stop old connections first
    this.stopRealTimeUpdates();

    // EventSource cannot attach the JWT Authorization header required by this
    // API. Use authenticated REST polling to avoid repeated CORS/401 failures.
    if (process.env.REACT_APP_ENABLE_NOTIFICATION_SSE !== 'true') {
      this.startPolling(onNotification);
      return;
    }

    try {
      const baseUrl = this.resolveRealtimeBaseUrl();
      this.eventSource = new EventSource(`${baseUrl}/api/notifications/stream`);

      this.eventSource.onmessage = (event) => {
        try {
          // Some backends send plain "heartbeat" strings, not JSON
          const raw = event.data;

          // ignore empty/keep-alive
          if (!raw || raw === 'heartbeat' || raw === 'keep-alive' || raw === 'keepalive') return;

          let payload: any;
          try {
            payload = JSON.parse(raw);
          } catch {
            // if not JSON, ignore (likely heartbeat)
            return;
          }

          // ✅ ignore heartbeat objects
          if (this.isHeartbeat(payload)) return;

          // ✅ accept either single notification or batch
          const maybeList = payload?.notifications;
          if (Array.isArray(maybeList)) {
            maybeList.forEach((n) => {
              if (this.isHeartbeat(n)) return;
              if (!this.isValidNotification(n)) return;
              onNotification(this.transformNotification(n));
            });
            return;
          }

          // single notification
          if (!this.isValidNotification(payload)) return;
          onNotification(this.transformNotification(payload));
        } catch (err) {
          console.error('Failed to handle SSE message:', err);
        }
      };

      this.eventSource.onerror = (error) => {
        console.error('EventSource error:', error);
        if (onError) onError(error);

        // Fallback to polling if SSE fails
        this.startPolling(onNotification);
      };
    } catch (error) {
      console.error('Failed to start EventSource:', error);
      this.startPolling(onNotification);
    }
  }

  // Fallback polling method
  private startPolling(onNotification: (notification: NotificationData) => void): void {
    if (this.pollingInterval) clearInterval(this.pollingInterval);

    let lastCheckTime = new Date();

    this.pollingInterval = setInterval(async () => {
      try {
        const response = await apiService.get<any>(
          `/api/notifications/recent?since=${lastCheckTime.toISOString()}`
        );

        const newNotifications: any[] = response.data?.data?.notifications || [];

        newNotifications.forEach((n: any) => {
          if (this.isHeartbeat(n)) return;
          if (!this.isValidNotification(n)) return;
          onNotification(this.transformNotification(n));
        });

        lastCheckTime = new Date();
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 15000);
  }

  // Stop real-time updates
  stopRealTimeUpdates(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }
}

export const notificationService = new NotificationService();
